import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, DataTable } from '@airclou/ui';
import { Users, ExternalLink, Layers } from 'lucide-react';
import { getApp, listAppUsers, type App, type AppUser } from '@/api/apps';
import { isRecordGone } from '@/api/errors';
import { ErrorState } from '../../components/state/ErrorState';
import { RefreshErrorBanner } from '../../components/state/RefreshErrorBanner';
import { ValueSkeleton } from '../../components/state/ValueSkeleton';
import { getAppUrl } from '@/utils/appUrls';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

export function AppDetailsPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { id } = useParams<{ id: string }>();
  const [app, setApp] = useState<App | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The app is gone. A retry cannot bring it back, so the page has nothing left
  // to show even while it holds the rows it loaded; only a successful load clears
  // it.
  const [gone, setGone] = useState(false);
  const [userSorting, setUserSorting] = useState<any>([]);

  // Which load is the current one, and which app the page is showing. `/apps/:id`
  // stays mounted across an app switch, so an answer for the app we left can
  // still be in flight; both have to agree. Written from the id-change effect,
  // not during render, so a render React abandons publishes nothing.
  const loadSeq = useRef(0);
  const shownId = useRef(id);

  const isCurrent = (seq: number, loadId?: string) =>
    seq === loadSeq.current && loadId === shownId.current;

  const loadData = async () => {
    if (!id) return;

    // Bail before claiming a sequence number: a stale caller that took the newest
    // one and then failed its own id check would orphan the live load.
    if (id !== shownId.current) return;

    const seq = ++loadSeq.current;
    const loadId = id;
    try {
      setLoading(true);
      // `error` is cleared on success rather than here, so the retry affordance
      // the operator just clicked stays on screen to show it is working.

      const [appResponse, usersResponse] = await Promise.all([
        getApp(id),
        listAppUsers(id),
      ]);
      if (!isCurrent(seq, loadId)) return;

      setApp(appResponse);
      setUsers(usersResponse);
      setGone(false);
      setError(null);
    } catch (err: any) {
      if (!isCurrent(seq, loadId)) return;
      // Latches: only a successful load clears it.
      if (isRecordGone(err)) setGone(true);
      setError(err.message || t('apps.failedToLoadDetails'));
    } finally {
      if (isCurrent(seq, loadId)) setLoading(false);
    }
  };

  useEffect(() => {
    shownId.current = id;
    // A different app under the same mounted route: drop the previous one's data
    // before loading the new one, so nothing from app A renders under app B's URL.
    setApp(null);
    setUsers([]);
    setGone(false);
    setError(null);
    loadData();
  }, [id]);

  const getStatusBadge = (status: App['status']) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            {t('common.active')}
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {t('common.inactive')}
          </span>
        );
      case 'maintenance':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            {t('apps.maintenance')}
          </span>
        );
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
            {t('common.admin')}
          </span>
        );
      case 'editor':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/15 text-primary dark:bg-primary/25 dark:text-[hsl(var(--primary-hover))]">
            {t('apps.editor')}
          </span>
        );
      case 'member':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            {t('common.member')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {role}
          </span>
        );
    }
  };

  const userColumns: any[] = [
    {
      id: 'user',
      header: t('common.user'),
      cell: (user: AppUser) => (
        <div>
          <p className="font-medium text-foreground">
            {user.user?.name || t('organization.unknownUser')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {user.user?.email || user.user_id}
          </p>
        </div>
      ),
    },
    {
      id: 'workspace',
      header: t('apps.workspace'),
      cell: (user: AppUser) => (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          {user.workspace_name ? (
            <>
              <Layers className="w-4 h-4" />
              {user.workspace_name}
            </>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">{t('apps.tenantLevelAccess')}</span>
          )}
        </div>
      ),
    },
    {
      id: 'role',
      header: t('common.role'),
      cell: (user: AppUser) => getRoleBadge(user.role),
    },
    {
      id: 'joined',
      header: t('common.joined'),
      cell: (user: AppUser) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(user.joined_at, timezone)}
        </div>
      ),
    },
  ];

  const appUrl = app ? getAppUrl(app.slug) : undefined;

  // `gone` stands on its own; otherwise this is only for "there is no app to
  // show". Keyed off `error` as well as `!loading` so the error state survives its
  // own retry instead of flashing the skeleton shell mid-flight.
  if (gone || (!app && (error || !loading))) {
    return (
      <div className="space-y-6">
        <ErrorState
          title={t('apps.failedToLoadDetails')}
          description={gone || !error ? t('apps.appNotFound') : error}
          onRetry={loadData}
          isRetrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between" aria-busy={!app}>
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-primary/15 dark:bg-primary/25">
              <Layers className="w-8 h-8 text-primary" />
            </div>
            <div>
              {app ? (
                <>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-foreground">{app.name}</h1>
                    {getStatusBadge(app.status)}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{app.description}</p>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {t('common.created')} {formatDateInTz(app.created_at, timezone)}
                  </div>
                </>
              ) : (
                // One placeholder per line above, in the same wrappers, so the
                // header is already its loaded height and nothing below moves.
                <>
                  <div className="h-8 w-48 animate-pulse rounded bg-muted" />
                  <div className="mt-1 text-sm"><ValueSkeleton className="w-64" /></div>
                  <div className="mt-2 text-sm"><ValueSkeleton className="w-40" /></div>
                </>
              )}
            </div>
          </div>
          {/* Only an active app with a known URL can be opened, so this button
              genuinely depends on the row and has nothing to show before it. */}
          {app?.status === 'active' && appUrl && (
            <Button onClick={() => window.open(appUrl, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('common.openApp')}
            </Button>
          )}
        </div>
      </div>

      {/* A reload that failed while the page still holds a good row: keep the page
          and offer a retry. An "app is gone" answer took the early return above. */}
      {error && app && (
        <RefreshErrorBanner message={error} onRetry={loadData} isRetrying={loading} />
      )}

      {/* Users — the card, its heading and its frame paint immediately; only the
          rows wait. */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="pb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('apps.usersWithAccess')}</h2>
              {/* The count waits for the rows: "0 users can access" on an app that
                  has some reads as an answer rather than as a page still loading. */}
              {app ? (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('apps.usersCanAccess', { count: users.length, plural: users.length !== 1 ? 's' : '' })}
                </p>
              ) : (
                <div className="mt-1"><ValueSkeleton className="w-48" /></div>
              )}
            </div>
          </div>
        </div>

        <div>
          {users.length === 0 && !loading ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t('apps.noUsersYet')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('apps.noUsersGranted')}
              </p>
            </div>
          ) : (
            <DataTable
              data={users}
              columns={userColumns}
              sorting={userSorting}
              onSortingChange={setUserSorting}
              isLoading={loading && users.length === 0}
            />
          )}
        </div>
      </div>
    </div>
  );
}
